
<script setup>
import {
  ref,
  onMounted,
  getCurrentInstance,
  watch,
  computed,
  defineProps,
} from 'vue';
import { useStore } from 'vuex';
import { MSEnum } from '@/commons/enumeration';
import avatarApi from '@/apis/other/avatarAPI';
import { showError } from '@/commons/globalMessage';
import { MsPopoverPosition } from '@/lib/vueFinalPopover/VueFinalPopover.vue';
import MsPopover from '@/components/popover/MsPopover.vue';
import commonFn from '@/commons/commonFunction';
import MsImage from '@/components/image/MsImage.vue';
import MsToolTip from '@/components/tooltip/MsToolTip.vue';
import { ModuleContext, ModuleNotification } from '@/stores/moduleConst.js';
import NotificationAPI from '@/apis/other/notificationAPI';
import { EnumNotify } from '@/commons/EnumNotify';
import popupUtil from '@/commons/popupUtil';
import EventBusGlobal, { GlobalEventName } from '@/commons/eventBusGlobal';
import { useRoute } from 'vue-router';
import commonFunction from '@/commons/commonFunction.js'
const props = defineProps({});

const { proxy } = getCurrentInstance();
const store = useStore();
const route = useRoute();

/**
 * Lấy avatar
 */
const getAvatar = (MISAID, size) => {
  return avatarApi.getAvatarLink(MISAID, size);
};

const isActiveToastMsgNotify = computed(() => {
  return store.state[ModuleNotification].isActiveToastMsgNotify;
});

const newNotify = computed(() => {
  return store.state[ModuleNotification].newNotify;
});

const amountNotifyUnread = computed(() => {
  return store.state[ModuleNotification].amountNotifyUnread;
});

watch(isActiveToastMsgNotify, (newVal) => {
  if (newVal) {
    // Tự động tắt sau 5s
    setTimeout(() => {
      store.dispatch('notification/toggleToastMsgNotify', false);
    }, 5000);
  }
});

const closeToast = () => {
  store.dispatch('notification/toggleToastMsgNotify', false);
};

/**
 * Xử lý khi chọn thông báo
 * created by VDGIang 17/02/2023
 */
const handleClickNotify = (notification) => {
  const me = this;
  const param = {
    appCode: MSEnum.AppCode,
  };

  if (!notification.IsView) {
    store.dispatch('notification/decreaseAmountNotifyUnread');
  }

  NotificationAPI.updateNotificationIsViewed(param, notification.Id);
  store.dispatch('notification/toggleToastMsgNotify', false);

  if (notification.FromAppCode == MSEnum.AppCode) {
    //Nếu xóa công việc, ủy quyền/bỏ ủy quyền thì không hiển thị gì
    if (
      notification.Action ==
        EnumNotify.Action.TaskGo2_AssignerDeleteTask ||
      notification.Action ==
        EnumNotify.Action.TaskGo2_AddAuthorizedPerson ||
      notification.Action ==
        EnumNotify.Action.TaskGo2_DeleteAuthorizedPerson
    ) {
      return;
    }
    showTaskPopup(notification.ExtraData.TaskID, notification.Action);
  }
};

const showTaskPopup = (taskID, action) => {
  // Nếu là xem bình luận thì scroll tới bình luận
  if (
    action == EnumNotify.Action.TaskGo2_Comment ||
    action == EnumNotify.Action.TaskGo2_Comment_Mention
  ) {
    store.commit('notification/setIsOpenFromCommentNotify', true);
  }

  if (route?.path?.includes('etask/task')) {
    EventBusGlobal.$emit(GlobalEventName.openTaskDetail, taskID);
  } else {
    popupUtil.show('PopupAddTask', {
      taskID: taskID,
      mode: taskID
        ? proxy.$ms.constant?.ModelState?.Update
        : proxy.$ms.constant?.ModelState?.Insert,
    });
  }
};
</script>

<template>
  <transition name="toast-msg-notify">
    <div
      v-if="isActiveToastMsgNotify && newNotify"
      class="ms-toast-msg-notify"
      @click="handleClickNotify(newNotify)"
    >
      <ms-tool-tip
        :position="{
          x: MsPopoverPosition.Left,
          y: MsPopoverPosition.Center,
          isOuterX: true,
          isOuterY: false,
          offsetX: -16,
        }"
        :has-arrow="true"
        :custom-style="{
          padding: '16px',
        }"
      >
        <div class="flex">
          <div class="avatar">
            <MsImage :src="getAvatar(newNotify?.ExtraData?.SenderMISAID, 40)" />
          </div>
          <div class="notify-content-container">
            <div
              class="content"
              v-html="commonFunction.sanitizeText(newNotify?.NotificationText || newNotify?.Message)"
            />
            <div class="time">
              {{ $ms.commonFn.convertTimeToString(newNotify?.Created) }}
            </div>
          </div>
        </div>
        <template #content>
          <div
            class="content-tooltip"
            v-html="commonFunction.sanitizeText(newNotify?.NotificationText || newNotify?.Message)"
          />
        </template>
      </ms-tool-tip>
      <div 
        v-tooltip="$t('i18nCommon.command.close')"
        class="icon-close pointer" 
        @click.prevent.stop="closeToast"
      >
        <div class="icon20 ic_close-grey-16" />
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
.content-tooltip {
  max-width: 400px;
}
.icon-close {
  position: absolute;
  right: 16px;
  top: 16px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: var(--grey-10);
    border-radius: 50%;
  }
}
.ms-toast-msg-notify {
  position: fixed;
  top: 54px;
  right: 24px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 0 10px 0 rgba($color: #000000, $alpha: 0.18);
  padding: 26px 40px 22px 16px;
  display: flex;
  cursor: pointer;
  max-height: 114px;
  width: 418px;
  z-index: 9999;

  .ms-toast-msg-notify__close-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px !important;
    height: 24px !important;
  }

  .avatar {
    position: relative;
    object-fit: cover;
    margin-right: 12px;
    height: 40px;
    width: 40px;
    border-radius: 50%;
    img:nth-child(1) {
      min-height: 40px;
      min-width: 40px;
      height: 40px;
      width: 40px;
      border-radius: 50%;
    }
    .icon-type {
      position: absolute;
      right: -2px;
      bottom: -3px;
    }
  }
  .content {
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 3;
    word-break: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  .time {
    line-height: 12px;
    font-size: 12px;
    margin: 4px 0;
  }
}

.toast-msg-notify-enter-active,
.toast-msg-notify-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.toast-msg-notify-enter {
  opacity: 0;
  transform: translateX(100%);
}
.toast-msg-notify-leave-to {
  opacity: 0;
}
</style>